import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { X, Download, Share2, Loader2, BadgeCheck } from "lucide-react";
import { api, apiError, track } from "@/lib/api";
import { toast } from "sonner";

export function BadgeCard({ b, refEl, big = false }) {
  const s = big
    ? { w: "w-[1080px] h-[1080px] p-[96px]", logo: "text-[64px]", label: "text-[26px]", name: "text-[88px]", career: "text-[60px]", score: "text-[150px]", meta: "text-[30px]", ring: "h-[240px] w-[240px]", icon: 120 }
    : { w: "aspect-square w-full p-7", logo: "text-xl", label: "text-[9px]", name: "text-3xl", career: "text-xl", score: "text-5xl", meta: "text-[11px]", ring: "h-16 w-16", icon: 34 };
  return (
    <div ref={refEl} className={`flex flex-col justify-between overflow-hidden bg-gradient-to-br from-emerald-950 via-teal-900 to-emerald-600 text-white ${s.w} ${big ? "" : "rounded-[28px]"}`}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex items-center justify-between">
        <span className={`font-display font-extrabold ${s.logo}`}>Nx<span className="text-amber-300">T</span></span>
        <span className={`font-mono text-white/60 ${s.meta}`}>{b.code}</span>
      </div>
      <div>
        <div className={`mx-auto flex items-center justify-center rounded-full border-4 border-emerald-300 bg-emerald-300/10 ${s.ring}`}><BadgeCheck size={s.icon} className="text-emerald-300" /></div>
        <div className={`mt-6 text-center font-bold uppercase tracking-[0.35em] text-white/60 ${s.label}`}>Interview Ready</div>
        <div className={`mt-3 text-center font-display font-extrabold leading-tight ${s.name}`}>{b.name}</div>
        <div className={`mt-3 text-center font-display font-bold text-emerald-200 ${s.career}`}>{b.career}</div>
        <div className={`mt-2 text-center font-display font-extrabold leading-none ${s.score}`}>{b.overall}<span className="text-white/50" style={{ fontSize: "0.35em" }}>/100</span></div>
        <div className={`mt-2 text-center text-white/70 ${s.meta}`}>{b.hire_signal}{b.level ? ` · ${b.level}` : ""} · 10-question AI mock interview</div>
      </div>
      <div className={`flex items-center justify-between border-t border-white/20 pt-4 text-white/60 ${s.meta}`}>
        <span>{b.updated_at || b.earned_at}</span><span className="font-bold uppercase tracking-[0.2em]">nxt · know what's next</span>
      </div>
    </div>
  );
}

export function InterviewBadge({ code, badge, onClose }) {
  const [b, setB] = useState(badge || null);
  const [busy, setBusy] = useState(false);
  const bigRef = useRef(null);
  useEffect(() => {
    if (badge) return;
    api.get(`/interview/badge/${code}`).then(({ data }) => setB(data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [code, badge, onClose]);

  const render = async () => { await document.fonts?.ready; return toPng(bigRef.current, { width: 1080, height: 1080, pixelRatio: 1, cacheBust: true }); };
  const file = `nxt-interview-ready-${(b?.career || "badge").replace(/\s+/g, "-").toLowerCase()}.png`;
  const download = async () => {
    setBusy(true);
    try { const url = await render(); const a = document.createElement("a"); a.href = url; a.download = file; a.click(); track("interview_badge_downloaded", { career: b.career }); }
    catch { toast.error("Could not render badge"); } finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const f = new File([blob], file, { type: "image/png" });
      const text = `I'm Interview Ready for ${b.career} — scored ${b.overall}/100 in NxT AI's 10-question mock interview ✅ Discover your Career DNA: ${window.location.origin}`;
      if (navigator.canShare && navigator.canShare({ files: [f] })) await navigator.share({ files: [f], text, title: "Interview Ready · NxT" });
      else { await navigator.clipboard.writeText(text); toast.success("Share text copied!"); }
      track("interview_badge_shared", { career: b.career });
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/80 p-4 backdrop-blur-sm sm:items-center" data-testid="interview-badge-modal" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
        <div className="mb-3 flex items-center justify-between text-white">
          <span className="font-display font-bold">Interview Ready badge</span>
          <button onClick={onClose} data-testid="badge-close" className="rounded-full bg-white/10 p-2 hover:bg-white/20"><X className="h-4 w-4" /></button>
        </div>
        {!b ? <div className="flex aspect-square items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div> : (
          <>
            <BadgeCard b={b} />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={download} disabled={busy} data-testid="badge-download-btn" className="flex items-center justify-center gap-2 rounded-full bg-emerald-400 py-3 text-sm font-bold text-slate-900 hover:bg-emerald-300 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download</button>
              <button onClick={share} disabled={busy} data-testid="badge-share-btn" className="flex items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-slate-900 hover:bg-white/90 disabled:opacity-60"><Share2 className="h-4 w-4" /> Share</button>
            </div>
            <div className="pointer-events-none fixed left-[-4000px] top-0" aria-hidden="true"><BadgeCard b={b} refEl={bigRef} big /></div>
          </>
        )}
      </motion.div>
    </div>
  );
}
